import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, MapPin, Loader2, ExternalLink, Download } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSEOLocations = () => {
  const { token } = useAuth();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [seeding, setSeeding] = useState(false);
  
  const [form, setForm] = useState({
    name: "",
    slug: "",
    description: "",
    meta_title: "",
    meta_description: "",
    image: "",
    is_active: true,
    priority: 0.7
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/seo-locations`);
      setLocations(response.data || []);
    } catch (error) {
      console.error("Error fetching SEO locations:", error);
      toast.error("Failed to load SEO locations");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Location name is required");
      return;
    }

    try {
      if (editingLocation) {
        await axios.put(
          `${API_URL}/api/seo-locations/${editingLocation.id}`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("SEO location updated successfully");
      } else {
        await axios.post(
          `${API_URL}/api/seo-locations`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("SEO location created successfully");
      }
      
      fetchLocations();
      resetForm();
      setShowModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save SEO location");
    }
  };

  const handleEdit = (location) => {
    setEditingLocation(location);
    setForm({
      name: location.name || "",
      slug: location.slug || "",
      description: location.description || "",
      meta_title: location.meta_title || "",
      meta_description: location.meta_description || "",
      image: location.image || "",
      is_active: location.is_active !== false,
      priority: location.priority || 0.7
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this SEO location?")) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/seo-locations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("SEO location deleted successfully");
      fetchLocations();
    } catch (error) {
      toast.error("Failed to delete SEO location");
    }
  };

  const handleSeedDefaults = async () => {
    try {
      setSeeding(true);
      const response = await axios.post(
        `${API_URL}/api/seo-locations/seed-defaults`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchLocations();
    } catch (error) {
      toast.error("Failed to seed default locations");
    } finally {
      setSeeding(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: "",
      slug: "",
      description: "",
      meta_title: "",
      meta_description: "",
      image: "",
      is_active: true,
      priority: 0.7
    });
    setEditingLocation(null);
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
  };

  const filteredLocations = locations.filter(loc =>
    loc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    loc.slug.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-seo-locations">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-foreground">
              Programmatic SEO
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage location pages for search engine optimization
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleSeedDefaults}
              disabled={seeding}
              className="rounded-full"
            >
              {seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Seed Defaults
            </Button>
            <Button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="bg-primary text-white hover:bg-primary/90 rounded-full"
              data-testid="add-seo-location-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Location
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Search locations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 rounded-full"
          />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-sm text-muted-foreground">Total Locations</p>
            <p className="text-2xl font-bold text-foreground">{locations.length}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-sm text-muted-foreground">Active</p>
            <p className="text-2xl font-bold text-green-600">{locations.filter(l => l.is_active).length}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-sm text-muted-foreground">Inactive</p>
            <p className="text-2xl font-bold text-gray-400">{locations.filter(l => !l.is_active).length}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-sm text-muted-foreground">In Sitemap</p>
            <p className="text-2xl font-bold text-primary">{locations.filter(l => l.is_active).length}</p>
          </div>
        </div>

        {/* Locations List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredLocations.length > 0 ? (
          <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
            <table className="w-full">
              <thead className="bg-secondary/30">
                <tr>
                  <th className="text-left py-4 px-6 font-medium text-foreground">Location</th>
                  <th className="text-left py-4 px-6 font-medium text-foreground">Slug</th>
                  <th className="text-left py-4 px-6 font-medium text-foreground">Priority</th>
                  <th className="text-left py-4 px-6 font-medium text-foreground">Status</th>
                  <th className="text-right py-4 px-6 font-medium text-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLocations.map((location) => (
                  <tr key={location.id} className="border-t hover:bg-secondary/10">
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <MapPin className="w-5 h-5 text-primary" />
                        <div>
                          <p className="font-medium text-foreground">{location.name}</p>
                          {location.meta_title && (
                            <p className="text-xs text-muted-foreground truncate max-w-xs">{location.meta_title}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <a
                        href={`/search/homestays-in-${location.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline flex items-center gap-1"
                      >
                        homestays-in-{location.slug}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </td>
                    <td className="py-4 px-6">
                      <span className="px-2 py-1 bg-secondary rounded text-sm">{location.priority}</span>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        location.is_active 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {location.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(location)}
                          className="h-8 w-8 p-0"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(location.id)}
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-20 bg-white rounded-2xl">
            <MapPin className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">No SEO Locations</h2>
            <p className="text-muted-foreground mb-6">
              {searchTerm ? "No locations match your search" : "Add locations for programmatic SEO pages"}
            </p>
            {!searchTerm && (
              <Button onClick={handleSeedDefaults} disabled={seeding} className="rounded-full">
                {seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Seed Default Konkan Locations
              </Button>
            )}
          </div>
        )}

        {/* Add/Edit Modal */}
        <Dialog open={showModal} onOpenChange={(open) => {
          setShowModal(open);
          if (!open) resetForm();
        }}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingLocation ? "Edit SEO Location" : "Add SEO Location"}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Location Name *</label>
                <Input
                  value={form.name}
                  onChange={(e) => {
                    setForm({ 
                      ...form, 
                      name: e.target.value,
                      slug: form.slug || generateSlug(e.target.value)
                    });
                  }}
                  placeholder="e.g., Dapoli"
                  required
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">URL Slug</label>
                <div className="flex items-center">
                  <span className="text-muted-foreground text-sm mr-2">/search/homestays-in-</span>
                  <Input
                    value={form.slug}
                    onChange={(e) => setForm({ ...form, slug: generateSlug(e.target.value) })}
                    placeholder="dapoli"
                    className="flex-1"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">Description</label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Brief description about this location for the search page..."
                  rows={3}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">Meta Title</label>
                <Input
                  value={form.meta_title}
                  onChange={(e) => setForm({ ...form, meta_title: e.target.value })}
                  placeholder="Homestays in Dapoli | Best Dapoli Homestays"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {form.meta_title.length}/60 characters (recommended)
                </p>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">Meta Description</label>
                <Textarea
                  value={form.meta_description}
                  onChange={(e) => setForm({ ...form, meta_description: e.target.value })}
                  placeholder="Discover authentic homestays in Dapoli, Konkan..."
                  rows={2}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {form.meta_description.length}/160 characters (recommended)
                </p>
              </div>

              <ImageUpload
                value={form.image}
                onChange={(url) => setForm({ ...form, image: url })}
                token={token}
                label="Location Image (for OG/Social sharing)"
              />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Sitemap Priority</label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="1"
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: parseFloat(e.target.value) || 0.7 })}
                  />
                  <p className="text-xs text-muted-foreground mt-1">0.0 to 1.0</p>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Status</label>
                  <div className="flex items-center gap-3 mt-2">
                    <Switch
                      checked={form.is_active}
                      onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
                    />
                    <span className="text-sm">{form.is_active ? 'Active' : 'Inactive'}</span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="rounded-full"
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-primary text-white hover:bg-primary/90 rounded-full">
                  {editingLocation ? "Update" : "Create"} Location
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminSEOLocations;
